import { Modal, ModalProps } from '@/modules/common/components/Modal';
import {
  ApplicationValidationReviewer,
  ApplicationValidationType,
  IApplicationWithOrderAndCourse,
} from '@/types/application';
import { UserRole } from '@/types/user';
import { Check, Close } from '@mui/icons-material';
import { Button, FormHelperText } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { ApplicationDetailsRow } from './ApplicationDetailsRow';
import { CommentBox } from './CommentBox';
import { CourseCard } from './CourseCard';
import { ReviewModalState } from './ReviewModal.types';

type TaxBenefitsReviewModalProps = Omit<
  ModalProps,
  'title' | 'subtitle' | 'children' | 'onClose'
> & {
  application: IApplicationWithOrderAndCourse;
  onClose: (submittedReview?: Partial<ReviewModalState>) => void;
  userRoles: UserRole[];
};

type FormValues = {
  [ApplicationValidationType.FlexibleRemuneration]: boolean;
  comments: string;
};

export function TaxBenefitsReviewModal({
  application,
  userRoles,
  onClose,
  ...props
}: TaxBenefitsReviewModalProps) {
  const {
    order: {
      userPaymentAmount: orderUserPaymentAmount,
      totalAmount: orderTotalAmount,
    } = {},
    validations,
  } = application;

  const formMethods = useForm<FormValues>({
    defaultValues: {
      [ApplicationValidationType.FlexibleRemuneration]: false,
      comments: '',
    },
  });
  const {
    handleSubmit,
    setValue,
    formState: { isSubmitting, errors },
  } = formMethods;

  const onSubmit = (data: FormValues) => {
    onClose({
      [ApplicationValidationType.FlexibleRemuneration]:
        data[ApplicationValidationType.FlexibleRemuneration],
      comments: data.comments,
    });
  };

  const taxBenefitsValidation = validations?.find(
    validation =>
      validation.name === ApplicationValidationType.FlexibleRemuneration &&
      validation.reviews.some(
        review =>
          (userRoles.includes(UserRole.Employer) &&
            review.reviewer === ApplicationValidationReviewer.Employer) ||
          (userRoles.includes(UserRole.TaxBenefitsProvider) &&
            review.reviewer ===
              ApplicationValidationReviewer.FlexibleRemunerationProvider),
      ),
  );

  if (!taxBenefitsValidation) {
    return null;
  }

  const totalAmount = (orderUserPaymentAmount?.amount || orderTotalAmount) ?? 0;

  const selectedCohort = application.course?.cohorts?.find(
    ({ _id }) => application.cohort === _id,
  );

  return (
    <Modal
      onClose={() => onClose(undefined)}
      showCloseButton
      title={
        <>
          Please review <span className="font-semibold">1 tax benefit</span>{' '}
          request.
        </>
      }
      classes={{
        paper: 'max-w-3xl',
      }}
      subTitle="If you click 'Approve,' the tax benefit will be automatically integrated with the application. If you choose to 'reject' it, the user will receive an email with further details."
      {...props}
      actions={
        <>
          <Button
            variant="outlined"
            type="submit"
            onClick={() =>
              setValue(ApplicationValidationType.FlexibleRemuneration, false)
            }
            endIcon={<Close />}
            form="tax-benefits-review-modal"
            disabled={isSubmitting}
          >
            Reject
          </Button>
          <Button
            variant="contained"
            type="submit"
            endIcon={<Check />}
            onClick={() => {
              setValue(ApplicationValidationType.FlexibleRemuneration, true);
            }}
            form="tax-benefits-review-modal"
            loading={isSubmitting}
          >
            Approve
          </Button>
        </>
      }
    >
      <FormProvider {...formMethods}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-4"
          id="tax-benefits-review-modal"
        >
          <CourseCard
            course={application.course}
            selectedCohort={selectedCohort}
          />
          <ApplicationDetailsRow
            user={application.user}
            applicationLearningBudgetAmount={undefined}
            remainingLearningBudget={undefined}
            totalAmount={totalAmount}
            orderUserPaymentAmount={orderUserPaymentAmount}
          />
          <CommentBox className="-mt-2" />
          <FormHelperText
            error={!!errors[ApplicationValidationType.FlexibleRemuneration]}
          >
            {typeof errors[ApplicationValidationType.FlexibleRemuneration]
              ?.message === 'string'
              ? errors[ApplicationValidationType.FlexibleRemuneration]?.message
              : undefined}
          </FormHelperText>
        </form>
      </FormProvider>
    </Modal>
  );
}
